import React from 'react';
import TransformSection from './common/transform-section';
import { registerInspector } from './inspector-registry';
import ColorField from './shared/color-field';
import InspectorRow from './shared/inspector-row';
import InspectorSection from './shared/inspector-section';
import MatchFilterSection from './shared/match-filter-section';
import MixedSelectField from './shared/mixed-select-field';
import NumberField from './shared/number-field';
import TextField from './shared/text-field';
import { useCommonFabricProp } from './shared/use-common-fabric-prop';
import type { ObjectInspectorProps } from './types';

const MIXED = '(разное)';

const FONT_FAMILIES = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Verdana', label: 'Verdana' },
];

const FONT_WEIGHTS = [
  { value: 'normal', label: 'Обычный' },
  { value: 'bold', label: 'Жирный' },
  { value: '300', label: 'Лёгкий (300)' },
  { value: '500', label: 'Средний (500)' },
  { value: '700', label: 'Жирный (700)' },
];

const TEXT_ALIGNS = [
  { value: 'left', label: 'Влево' },
  { value: 'center', label: 'По центру' },
  { value: 'right', label: 'Вправо' },
  { value: 'justify', label: 'По ширине' },
];

const TextboxInspector: React.FC<ObjectInspectorProps> = ({ objects, editor }) => {
  const text = useCommonFabricProp<string>(objects, 'text', editor);
  const fontSize = useCommonFabricProp<number>(objects, 'fontSize', editor);
  const fontFamily = useCommonFabricProp<string>(objects, 'fontFamily', editor);
  const fontWeight = useCommonFabricProp<string>(objects, 'fontWeight', editor);
  const textAlign = useCommonFabricProp<string>(objects, 'textAlign', editor);
  const lineHeight = useCommonFabricProp<number>(objects, 'lineHeight', editor);
  const fill = useCommonFabricProp<string>(objects, 'fill', editor);
  const opacity = useCommonFabricProp<number>(objects, 'opacity', editor);

  return (
    <>
      <TransformSection objects={objects} editor={editor} />
      <InspectorSection title="Содержимое">
        <InspectorRow label="Текст">
          <TextField
            value={text.isMixed ? '' : text.value}
            onChange={text.setValue}
            placeholder={text.isMixed ? MIXED : undefined}
            multiline
          />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title="Типографика">
        <InspectorRow label="Шрифт">
          <MixedSelectField
            value={fontFamily.value}
            isMixed={fontFamily.isMixed}
            onChange={fontFamily.setValue}
            options={FONT_FAMILIES}
          />
        </InspectorRow>
        <InspectorRow label="Размер">
          <NumberField
            value={fontSize.isMixed ? undefined : fontSize.value}
            onChange={fontSize.setValue}
            min={1}
            placeholder={fontSize.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label="Начертание">
          <MixedSelectField
            value={fontWeight.value === undefined ? undefined : String(fontWeight.value)}
            isMixed={fontWeight.isMixed}
            onChange={fontWeight.setValue}
            options={FONT_WEIGHTS}
          />
        </InspectorRow>
        <InspectorRow label="Выравнивание">
          <MixedSelectField
            value={textAlign.value}
            isMixed={textAlign.isMixed}
            onChange={textAlign.setValue}
            options={TEXT_ALIGNS}
          />
        </InspectorRow>
        <InspectorRow label="Высота строки">
          <NumberField
            value={lineHeight.isMixed ? undefined : lineHeight.value}
            onChange={lineHeight.setValue}
            step={0.1}
            min={0.1}
            placeholder={lineHeight.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title="Внешний вид">
        <InspectorRow label="Цвет">
          <ColorField
            value={fill.isMixed ? undefined : fill.value}
            onChange={fill.setValue}
            placeholder={fill.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label="Прозрачность">
          <NumberField
            value={opacity.isMixed ? undefined : opacity.value}
            onChange={opacity.setValue}
            step={0.05}
            min={0}
            max={1}
            placeholder={opacity.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      </InspectorSection>
      <MatchFilterSection objects={objects} editor={editor} />
    </>
  );
};

export default TextboxInspector;

registerInspector('textbox', TextboxInspector);
registerInspector('i-text', TextboxInspector);
registerInspector('text', TextboxInspector);
